import {GitMergeIcon, GitPullRequestClosedIcon} from '@primer/octicons-react'
import {ForYouItem} from '../shared/ForYouItem'
import {useMergeabilityData, type MergeBoxPullRequest} from '../mergeability/use-mergeability-data'
import {BranchName, Button} from '@primer/react'
import {branchPath} from '@github-ui/paths'
import {clsx} from 'clsx'
import styles from './DeleteHeadBranch.module.css'
import {useDeleteHeadRefMutation} from '@github-ui/mergebox/use-delete-head-ref-mutation'
import {FailureDialog} from '../../../FailureDialog'
import {announce} from '@github-ui/aria-live'
import {useRef} from 'react'
import {MergedDescription} from './MergedDescription'
import {ReopenPullRequestAction} from './ReopenPullRequest'
import {RevertPRAction} from './RevertPRAction'
import {findPreviousTabbableElementOutsideRow} from './focus-utils'

export function shouldShowDeleteHeadBranch(data: ReturnType<typeof useMergeabilityData>) {
  const {mergeBoxPullRequest, isClosedOrMerged} = data ?? {}

  return Boolean(mergeBoxPullRequest && isClosedOrMerged && mergeBoxPullRequest.viewerCanDeleteHeadRef)
}

export function DeleteHeadBranch({canReopen}: {canReopen?: boolean}) {
  const data = useMergeabilityData()

  if (!data) {
    return null
  }

  const {mergeBoxPullRequest, isClosedOrMerged} = data

  if (!isClosedOrMerged || !mergeBoxPullRequest.viewerCanDeleteHeadRef) {
    return null
  }

  if (mergeBoxPullRequest.state === 'MERGED') {
    return <DeleteMergedHeadBranch pullRequest={mergeBoxPullRequest} />
  }

  return <DeleteClosedHeadBranch canReopen={canReopen} pullRequest={mergeBoxPullRequest} />
}

function DeleteMergedHeadBranch({pullRequest}: {pullRequest: MergeBoxPullRequest}) {
  return (
    <ForYouItem
      itemId="delete-head-branch"
      visual={{kind: 'icon', icon: GitMergeIcon, tone: 'done', glyphSize: 12}}
      title="Merged"
      subText={
        <MergedDescription
          trailing={
            <>
              Delete the{' '}
              <span data-testid="for-you-item-branch">
                <HeadBranchLink refName={pullRequest.headRefName} repository={pullRequest.headRepository} />
              </span>{' '}
              branch to tidy up.
            </>
          }
        />
      }
      actions={[
        {
          id: 'delete',
          content: <DeleteBranchAction />,
        },
        {id: 'revert', content: <RevertPRAction />},
      ]}
    />
  )
}

function DeleteClosedHeadBranch({canReopen, pullRequest}: {canReopen?: boolean; pullRequest: MergeBoxPullRequest}) {
  return (
    <ForYouItem
      itemId="delete-head-branch"
      visual={{kind: 'icon', icon: GitPullRequestClosedIcon, tone: 'danger', glyphSize: 12}}
      title="Closed"
      subText={
        <>
          This pull request is closed, but the{' '}
          <HeadBranchLink refName={pullRequest.headRefName} repository={pullRequest.headRepository} /> branch has
          unmerged commits.
        </>
      }
      actions={[
        {
          id: 'delete',
          content: <DeleteBranchAction />,
        },
        {id: 'reopen', content: canReopen ? <ReopenPullRequestAction /> : null},
      ]}
    />
  )
}

function HeadBranchLink({
  refName,
  repository,
}: {
  refName: string
  repository: {ownerLogin: string; name: string} | null
}) {
  if (!repository?.name) {
    return (
      <BranchName as="span" className={clsx(styles.branchName, 'd-inline wb-break-all')}>
        {refName}
      </BranchName>
    )
  }

  return (
    <BranchName
      href={branchPath({owner: repository.ownerLogin, repo: repository.name, branch: refName})}
      className={clsx(styles.branchName, 'd-inline wb-break-all Link--inTextBlock')}
    >
      {refName}
    </BranchName>
  )
}

function DeleteBranchAction() {
  const deleteButtonRef = useRef<HTMLButtonElement>(null)
  const deleteHeadRefMutation = useDeleteHeadRefMutation({
    onSuccess: () => {
      // Move focus before query invalidation removes the action from the DOM.
      findPreviousTabbableElementOutsideRow(deleteButtonRef.current)?.focus()
      announce('Branch deleted')
    },
  })

  return (
    <>
      <Button
        ref={deleteButtonRef}
        loading={deleteHeadRefMutation.isPending}
        loadingAnnouncement="Deleting branch"
        onClick={() => deleteHeadRefMutation.mutate()}
        size="small"
        labelWrap
      >
        Delete branch
      </Button>

      {deleteHeadRefMutation.isError && (
        <FailureDialog
          title="Failed to delete branch"
          message="An error occurred while deleting the head branch. Please try again."
          details={deleteHeadRefMutation.error.message}
          onRetry={() => deleteHeadRefMutation.mutate()}
          onDismiss={() => deleteHeadRefMutation.reset()}
        />
      )}
    </>
  )
}
